'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import ToolSEO from '@/components/ToolSEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import SocialShareButtons from '@/components/SocialShareButtons';
import UniversalBookmark from '@/components/UniversalBookmark';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

interface InfrastructureInputs {
  // Compute
  cpuCores: number;
  ramGB: number;
  instanceCount: number;

  // Storage
  storageGB: number;
  backupGB: number;

  // Database
  databaseType: 'none' | 'managed' | 'self-hosted';
  databaseSize: number; // GB

  // Network
  monthlyTrafficGB: number;

  // Additional services
  loadBalancer: boolean;
  cdn: boolean;
  monitoring: boolean;

  // Scale projection
  projectionMonths: number;
  monthlyGrowthRate: number; // percentage
}

interface CloudCosts {
  compute: number;
  storage: number;
  database: number;
  network: number;
  additional: number;
  total: number;
}

interface CostEstimate {
  aws: CloudCosts;
  gcp: CloudCosts;
  azure: CloudCosts;
  projected: {
    current: number;
    month3: number;
    month6: number;
    month12: number;
  };
}

export default function CloudCostEstimatorPage() {
  const [inputs, setInputs] = useState<InfrastructureInputs>({
    cpuCores: 4,
    ramGB: 16,
    instanceCount: 2,
    storageGB: 100,
    backupGB: 100,
    databaseType: 'managed',
    databaseSize: 50,
    monthlyTrafficGB: 1000,
    loadBalancer: true,
    cdn: true,
    monitoring: true,
    projectionMonths: 12,
    monthlyGrowthRate: 10,
  });
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'cloud-cost-estimator',
    toolCategory: 'calculator',
  });

  const updateInput = <K extends keyof InfrastructureInputs>(key: K, value: InfrastructureInputs[K]) => {
    setInputs(prev => ({ ...prev, [key]: value }));
  };

  const estimate = useMemo((): CostEstimate => {
    const {
      cpuCores,
      ramGB,
      instanceCount,
      storageGB,
      backupGB,
      databaseType,
      databaseSize,
      monthlyTrafficGB,
      loadBalancer,
      cdn,
      monitoring,
      projectionMonths,
      monthlyGrowthRate,
    } = inputs;

    // Real pricing data (2025 rates, US East/Central regions)

    // AWS costs (us-east-1)
    const awsCosts: CloudCosts = {
      compute: 0,
      storage: 0,
      database: 0,
      network: 0,
      additional: 0,
      total: 0,
    };

    // EC2 pricing: t3.xlarge (4 vCPU, 16GB) = $0.1664/hr = $121/mo
    // Scale based on CPU/RAM ratio
    const awsInstanceCost = Math.ceil((cpuCores / 4) * (ramGB / 16)) * 121;
    awsCosts.compute = awsInstanceCost * instanceCount;

    // EBS gp3: $0.08/GB-month
    awsCosts.storage = (storageGB + backupGB) * 0.08;

    // RDS MySQL db.t3.medium (2 vCPU, 4GB): $0.082/hr = $60/mo
    // RDS storage: $0.115/GB-month
    if (databaseType === 'managed') {
      awsCosts.database = 60 + (databaseSize * 0.115);
    } else if (databaseType === 'self-hosted') {
      awsCosts.database = databaseSize * 0.08; // EBS storage only
    }

    // Data transfer: First 10GB free, then $0.09/GB
    awsCosts.network = Math.max(0, monthlyTrafficGB - 10) * 0.09;

    // Additional services (real pricing)
    if (loadBalancer) awsCosts.additional += 16.20; // ALB: $0.0225/hr
    if (cdn) {
      // CloudFront: $0.085/GB for first 10TB
      const cdnTraffic = monthlyTrafficGB * 0.8; // 80% cached
      awsCosts.additional += cdnTraffic * 0.085;
    }
    if (monitoring) awsCosts.additional += 10; // CloudWatch basic

    awsCosts.total = Object.values(awsCosts).reduce((sum, val) => sum + val, 0);

    // GCP costs (us-central1)
    const gcpCosts: CloudCosts = {
      compute: 0,
      storage: 0,
      database: 0,
      network: 0,
      additional: 0,
      total: 0,
    };

    // n2-standard-4 (4 vCPU, 16GB): $0.1949/hr = $142/mo on-demand
    const gcpInstanceCost = Math.ceil((cpuCores / 4) * (ramGB / 16)) * 142;
    gcpCosts.compute = gcpInstanceCost * instanceCount;

    // Persistent Disk Standard: $0.04/GB-month
    // Persistent Disk SSD: $0.17/GB-month (using standard for cost estimate)
    gcpCosts.storage = (storageGB + backupGB) * 0.04;

    // Cloud SQL MySQL db-n1-standard-1 (1 vCPU, 3.75GB): $0.0960/hr = $70/mo
    // Cloud SQL storage: $0.17/GB-month
    if (databaseType === 'managed') {
      gcpCosts.database = 70 + (databaseSize * 0.17);
    } else if (databaseType === 'self-hosted') {
      gcpCosts.database = databaseSize * 0.04; // Persistent disk
    }

    // Network egress: First 1GB free, then $0.12/GB (0-1TB tier)
    gcpCosts.network = Math.max(0, monthlyTrafficGB - 1) * 0.12;

    // Additional services (real pricing)
    if (loadBalancer) gcpCosts.additional += 18; // $0.025/hr
    if (cdn) {
      // Cloud CDN: $0.08/GB for first 10TB
      const cdnTraffic = monthlyTrafficGB * 0.8;
      gcpCosts.additional += cdnTraffic * 0.08;
    }
    if (monitoring) gcpCosts.additional += 8; // Cloud Monitoring basic

    gcpCosts.total = Object.values(gcpCosts).reduce((sum, val) => sum + val, 0);

    // Azure costs (East US)
    const azureCosts: CloudCosts = {
      compute: 0,
      storage: 0,
      database: 0,
      network: 0,
      additional: 0,
      total: 0,
    };

    // D4s v3 (4 vCPU, 16GB): $0.192/hr = $140/mo
    const azureInstanceCost = Math.ceil((cpuCores / 4) * (ramGB / 16)) * 140;
    azureCosts.compute = azureInstanceCost * instanceCount;

    // Managed Disks Standard SSD: $0.05/GB-month
    // Premium SSD: $0.135/GB-month (using standard for cost estimate)
    azureCosts.storage = (storageGB + backupGB) * 0.05;

    // Azure Database for MySQL (2 vCPU, 4GB): $0.088/hr = $64/mo
    // Storage: $0.115/GB-month
    if (databaseType === 'managed') {
      azureCosts.database = 64 + (databaseSize * 0.115);
    } else if (databaseType === 'self-hosted') {
      azureCosts.database = databaseSize * 0.05; // Managed disk
    }

    // Data transfer: First 5GB free, then $0.087/GB
    azureCosts.network = Math.max(0, monthlyTrafficGB - 5) * 0.087;

    // Additional services (real pricing)
    if (loadBalancer) azureCosts.additional += 18; // $0.025/hr
    if (cdn) {
      // Azure CDN: $0.081/GB for first 10TB
      const cdnTraffic = monthlyTrafficGB * 0.8;
      azureCosts.additional += cdnTraffic * 0.081;
    }
    if (monitoring) azureCosts.additional += 12; // Azure Monitor basic

    azureCosts.total = Object.values(azureCosts).reduce((sum, val) => sum + val, 0);

    // Projected costs (using AWS as baseline)
    const growthFactor = 1 + (monthlyGrowthRate / 100);
    const currentTotal = awsCosts.total;

    const projected = {
      current: currentTotal,
      month3: currentTotal * Math.pow(growthFactor, 3),
      month6: currentTotal * Math.pow(growthFactor, 6),
      month12: currentTotal * Math.pow(growthFactor, 12),
    };

    return {
      aws: awsCosts,
      gcp: gcpCosts,
      azure: azureCosts,
      projected,
    };
  }, [inputs]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const cheapest = useMemo(() => {
    const costs = [
      { name: 'AWS', total: estimate.aws.total },
      { name: 'GCP', total: estimate.gcp.total },
      { name: 'Azure', total: estimate.azure.total },
    ];
    return costs.sort((a, b) => a.total - b.total)[0];
  }, [estimate]);

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const providerScores: Record<string, number> = {
      AWS: estimate.aws.total,
      GCP: estimate.gcp.total,
      Azure: estimate.azure.total,
    };

    const recommendations = [
      {
        id: 'cheapest-provider',
        title: `Use ${cheapest.name} for lowest cost`,
        priority: 'high' as const,
        description: `${cheapest.name} is ${formatCurrency(cheapest.total)}/month, saving ${formatCurrency(Math.max(estimate.aws.total, estimate.gcp.total, estimate.azure.total) - cheapest.total)}/month vs most expensive option`,
      },
      ...(inputs.monthlyGrowthRate > 15 ? [{
        id: 'high-growth',
        title: 'Consider reserved instances',
        priority: 'medium' as const,
        description: `With ${inputs.monthlyGrowthRate}% monthly growth, reserved instances could save 30-70%`,
      }] : []),
    ];

    const assessment = await saveResult({
      name: `Cloud Cost Estimate - ${new Date().toLocaleDateString()}`,
      score_overall: cheapest.total,
      scores: providerScores,
      answers: {
        infrastructure: inputs,
      },
      recommendations,
      metadata: {
        cheapestProvider: cheapest.name,
        monthlyCost: cheapest.total,
        annualCost: cheapest.total * 12,
        projected12MonthCost: estimate.projected.month12,
        costBreakdown: {
          aws: estimate.aws,
          gcp: estimate.gcp,
          azure: estimate.azure,
        },
        infrastructure: {
          cpuCores: inputs.cpuCores,
          ramGB: inputs.ramGB,
          instanceCount: inputs.instanceCount,
          storageGB: inputs.storageGB,
          databaseType: inputs.databaseType,
          monthlyTrafficGB: inputs.monthlyTrafficGB,
        },
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  const TOOL_NAME = 'Cloud Cost Estimator';
  const TOOL_SLUG = 'cloud-cost-estimator';
  const TOOL_DESCRIPTION = 'Estimate infrastructure costs across AWS, GCP, and Azure';

  return (
    <>
      <ToolSEO
        toolName={TOOL_NAME}
        toolSlug={TOOL_SLUG}
        description={TOOL_DESCRIPTION}
        category="calculator"
      />

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumbs */}
          <Breadcrumbs
            items={[
              { name: 'Tools', url: '/tools' },
              { name: TOOL_NAME, url: `/tools/${TOOL_SLUG}` },
            ]}
          />

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-4xl">💰</span>
                  <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                    {TOOL_NAME}
                  </h1>
                </div>
                <p className="text-xl text-foreground-secondary">
                  {TOOL_DESCRIPTION}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 shrink-0">
                <UniversalBookmark
                  contentType="tool"
                  contentSlug={TOOL_SLUG}
                  contentTitle={TOOL_NAME}
                  variant="icon"
                />
                <SocialShareButtons
                  url={`https://theartofcto.com/tools/${TOOL_SLUG}`}
                  title={TOOL_NAME}
                  description={TOOL_DESCRIPTION}
                  variant="dropdown"
                />
              </div>
            </div>
          </div>

        {/* Instructions */}
        <div className="bg-accent-muted border border-border rounded-lg p-6 mb-8">
          <h3 className="font-bold text-accent mb-2">How to use this calculator</h3>
          <ol className="text-foreground-secondary text-sm space-y-2 list-decimal list-inside">
            <li>Configure your infrastructure requirements (compute, storage, database)</li>
            <li>Select additional services you need (load balancer, CDN, monitoring)</li>
            <li>Set your growth projections to see future costs</li>
            <li>Compare costs across AWS, GCP, and Azure</li>
          </ol>
          <p className="text-xs text-foreground-tertiary mt-4">
            Note: Costs based on 2025 on-demand pricing (AWS us-east-1, GCP us-central1, Azure East US).
            Actual costs may vary with reserved instances (up to 72% savings), savings plans, commitment discounts, spot instances, and regional differences.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column: Inputs */}
          <div className="space-y-6">
            {/* Compute */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Compute</h3>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-foreground mb-2">
                  CPU Cores per Instance
                </label>
                <input
                  type="number" inputMode="numeric"
                  min="1"
                  max="128"
                  value={inputs.cpuCores}
                  onChange={(e) => updateInput('cpuCores', parseInt(e.target.value) || 1)}
                  className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-foreground mb-2">
                  RAM (GB) per Instance
                </label>
                <input
                  type="number" inputMode="numeric"
                  min="1"
                  max="512"
                  value={inputs.ramGB}
                  onChange={(e) => updateInput('ramGB', parseInt(e.target.value) || 1)}
                  className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Number of Instances
                </label>
                <input
                  type="number" inputMode="numeric"
                  min="1"
                  max="100"
                  value={inputs.instanceCount}
                  onChange={(e) => updateInput('instanceCount', parseInt(e.target.value) || 1)}
                  className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                />
              </div>
            </div>

            {/* Storage */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Storage</h3>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Primary Storage (GB)
                </label>
                <input
                  type="number" inputMode="numeric"
                  min="0"
                  step="10"
                  value={inputs.storageGB}
                  onChange={(e) => updateInput('storageGB', parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Backup Storage (GB)
                </label>
                <input
                  type="number" inputMode="numeric"
                  min="0"
                  step="10"
                  value={inputs.backupGB}
                  onChange={(e) => updateInput('backupGB', parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                />
              </div>
            </div>

            {/* Database */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Database</h3>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Database Type
                </label>
                <select
                  value={inputs.databaseType}
                  onChange={(e) => updateInput('databaseType', e.target.value as InfrastructureInputs['databaseType'])}
                  className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                >
                  <option value="none">None</option>
                  <option value="managed">Managed (RDS/Cloud SQL/Azure DB)</option>
                  <option value="self-hosted">Self-Hosted</option>
                </select>
              </div>

              {inputs.databaseType !== 'none' && (
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Database Size (GB)
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    min="0"
                    step="10"
                    value={inputs.databaseSize}
                    onChange={(e) => updateInput('databaseSize', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                  />
                </div>
              )}
            </div>

            {/* Network */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Network</h3>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Monthly Outbound Traffic (GB)
                </label>
                <input
                  type="number" inputMode="numeric"
                  min="0"
                  step="100"
                  value={inputs.monthlyTrafficGB}
                  onChange={(e) => updateInput('monthlyTrafficGB', parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                />
              </div>
            </div>

            {/* Additional Services */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Additional Services</h3>

              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={inputs.loadBalancer}
                    onChange={(e) => updateInput('loadBalancer', e.target.checked)}
                    className="w-5 h-5"
                  />
                  <span className="text-foreground">Load Balancer</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={inputs.cdn}
                    onChange={(e) => updateInput('cdn', e.target.checked)}
                    className="w-5 h-5"
                  />
                  <span className="text-foreground">CDN</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={inputs.monitoring}
                    onChange={(e) => updateInput('monitoring', e.target.checked)}
                    className="w-5 h-5"
                  />
                  <span className="text-foreground">Monitoring & Logging</span>
                </label>
              </div>
            </div>

            {/* Growth Projection */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Growth Projection</h3>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Monthly Growth Rate: {inputs.monthlyGrowthRate}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="50"
                  value={inputs.monthlyGrowthRate}
                  onChange={(e) => updateInput('monthlyGrowthRate', parseInt(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* Right Column: Results */}
          <div className="space-y-6">
            {/* Cost Comparison */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Monthly Cost Comparison</h3>

              <div className="space-y-4">
                {/* AWS */}
                <div className={`p-4 rounded-lg border ${cheapest.name === 'AWS' ? 'border-accent bg-accent-muted' : 'border-border'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">☁️</span>
                      <h4 className="font-bold text-foreground">AWS</h4>
                      {cheapest.name === 'AWS' && <span className="text-xs bg-accent text-accent-foreground px-2 py-1 rounded">Cheapest</span>}
                    </div>
                    <div className="text-2xl font-bold text-accent">
                      {formatCurrency(estimate.aws.total)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-foreground-secondary">
                    <div>Compute: {formatCurrency(estimate.aws.compute)}</div>
                    <div>Storage: {formatCurrency(estimate.aws.storage)}</div>
                    <div>Database: {formatCurrency(estimate.aws.database)}</div>
                    <div>Network: {formatCurrency(estimate.aws.network)}</div>
                    <div>Additional: {formatCurrency(estimate.aws.additional)}</div>
                  </div>
                </div>

                {/* GCP */}
                <div className={`p-4 rounded-lg border ${cheapest.name === 'GCP' ? 'border-accent bg-accent-muted' : 'border-border'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">☁️</span>
                      <h4 className="font-bold text-foreground">GCP</h4>
                      {cheapest.name === 'GCP' && <span className="text-xs bg-accent text-accent-foreground px-2 py-1 rounded">Cheapest</span>}
                    </div>
                    <div className="text-2xl font-bold text-accent">
                      {formatCurrency(estimate.gcp.total)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-foreground-secondary">
                    <div>Compute: {formatCurrency(estimate.gcp.compute)}</div>
                    <div>Storage: {formatCurrency(estimate.gcp.storage)}</div>
                    <div>Database: {formatCurrency(estimate.gcp.database)}</div>
                    <div>Network: {formatCurrency(estimate.gcp.network)}</div>
                    <div>Additional: {formatCurrency(estimate.gcp.additional)}</div>
                  </div>
                </div>

                {/* Azure */}
                <div className={`p-4 rounded-lg border ${cheapest.name === 'Azure' ? 'border-accent bg-accent-muted' : 'border-border'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">☁️</span>
                      <h4 className="font-bold text-foreground">Azure</h4>
                      {cheapest.name === 'Azure' && <span className="text-xs bg-accent text-accent-foreground px-2 py-1 rounded">Cheapest</span>}
                    </div>
                    <div className="text-2xl font-bold text-accent">
                      {formatCurrency(estimate.azure.total)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-foreground-secondary">
                    <div>Compute: {formatCurrency(estimate.azure.compute)}</div>
                    <div>Storage: {formatCurrency(estimate.azure.storage)}</div>
                    <div>Database: {formatCurrency(estimate.azure.database)}</div>
                    <div>Network: {formatCurrency(estimate.azure.network)}</div>
                    <div>Additional: {formatCurrency(estimate.azure.additional)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Cost Projection */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Cost Projection (AWS)</h3>
              <p className="text-sm text-foreground-tertiary mb-4">
                Based on {inputs.monthlyGrowthRate}% monthly growth rate
              </p>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-background rounded">
                  <span className="text-foreground-secondary">Current</span>
                  <span className="text-accent font-bold">{formatCurrency(estimate.projected.current)}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-background rounded">
                  <span className="text-foreground-secondary">Month 3</span>
                  <span className="text-accent font-bold">{formatCurrency(estimate.projected.month3)}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-background rounded">
                  <span className="text-foreground-secondary">Month 6</span>
                  <span className="text-accent font-bold">{formatCurrency(estimate.projected.month6)}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-background rounded">
                  <span className="text-foreground-secondary">Month 12</span>
                  <span className="text-accent font-bold">{formatCurrency(estimate.projected.month12)}</span>
                </div>
              </div>

              <div className="mt-4 p-3 bg-accent-muted rounded border border-border">
                <p className="text-xs text-foreground-secondary">
                  Annual projected spend: <span className="text-accent font-bold">{formatCurrency(estimate.projected.month12 * 12)}</span>
                </p>
              </div>
            </div>

            {/* Cost Savings Tips */}
            <div className="bg-accent-muted border border-border rounded-lg p-6">
              <h3 className="text-lg font-bold text-accent mb-3">Cost Optimization Tips</h3>
              <ul className="space-y-2 text-foreground-secondary text-sm">
                <li>• Use reserved instances or savings plans for 30-70% discounts</li>
                <li>• Enable auto-scaling to match capacity with demand</li>
                <li>• Use spot instances for non-critical workloads</li>
                <li>• Implement lifecycle policies for storage and backups</li>
                <li>• Consider serverless options for variable workloads</li>
                <li>• Set up cost alerts and budgets</li>
                <li>• Review and right-size instances regularly</li>
              </ul>
            </div>

            {/* Command Center Save Button */}
            <div className="flex flex-wrap gap-4">
              {commandCenterEnabled && !saved && (
                <button
                  onClick={handleSaveToCommandCenter}
                  disabled={saving}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save to Command Center'}
                </button>
              )}
              {saved && (
                <div className="px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Saved to Command Center
                </div>
              )}
              {saveError && (
                <div className="px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm">
                  {saveError}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Additional Resources */}
        <div className="mt-8 bg-surface rounded-lg border border-border p-6">
          <h3 className="text-lg font-bold text-foreground mb-3">Related Resources</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Link
              href="/architectures"
              className="p-4 bg-background rounded border border-border-muted hover:border-border transition-colors"
            >
              <h4 className="font-bold text-foreground mb-1">Architecture Templates →</h4>
              <p className="text-sm text-foreground-tertiary">
                Cloud architecture patterns and best practices
              </p>
            </Link>
            <Link
              href="/comparisons"
              className="p-4 bg-background rounded border border-border-muted hover:border-border transition-colors"
            >
              <h4 className="font-bold text-foreground mb-1">Cloud Comparisons →</h4>
              <p className="text-sm text-foreground-tertiary">
                Detailed comparisons of AWS, GCP, and Azure services
              </p>
            </Link>
          </div>
        </div>
        </div>
      </div>
    </>
  );
}
